import React, { useState } from "react";
import { Row, Col, Table, Form, Dropdown } from "react-bootstrap";
import {
  LuRefreshCw,
  LuPower,
  LuSearch,
  LuFilter,
  LuCalendar,
  LuDownload,
  LuMoreVertical,
  LuArrowLeft,
  LuChevronLeft,
  LuChevronRight,
  LuFileText,
  LuClock,
  LuBike,
  LuAlertCircle,
  LuCheckCircle2,
  LuReceipt,
  LuRotateCw,
} from "react-icons/lu";
import {
  DeliveryPartner,
  IntegratedOrder,
  SettlementSummary,
  SettlementTransaction,
} from "../types";

interface ConnectedPartnerDashboardProps {
  partner: DeliveryPartner;
  orders: IntegratedOrder[];
  syncing: boolean;
  onSync: () => void;
  onDisconnect: () => void;
  onBackToCatalog: () => void;
  onOpenLogs?: (order: IntegratedOrder) => void;
  onRetryOrderSync?: (orderId: number) => void;
  currentPage?: number;
  totalPages?: number;
  totalCount?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
  onSearchChange?: (search: string) => void;
  onStatusFilterChange?: (status: string) => void;
  onDateRangeChange?: (dateRange: string) => void;
  stats?: any;
  kycStatus?: "PENDING" | "APPROVED" | "REJECTED" | null;
  merchantId?: string | null;
  settlementSummary?: SettlementSummary | null;
  settlementTransactions?: SettlementTransaction[];
  loadingLedger?: boolean;
  ledgerPage?: number;
  ledgerTotalPages?: number;
  ledgerTotalCount?: number;
  onLedgerPageChange?: (page: number) => void;
  onLedgerEventTypeChange?: (eventType: string) => void;
}

// Helper CSV Exporters


const downloadOrdersCSV = (items: IntegratedOrder[]) => {
  if (!items || items.length === 0) return;
  const headers = ["Order ID", "Product", "SKU", "Customer", "Phone", "Order Date", "Amount", "Status"];
  const rows = items.map((o) => [
    o.order_id_display || `#ORD-${o.id}`,
    `"${(o.product_name || "Integrated Item").replace(/"/g, '""')}"`,
    o.product_sku || "N/A",
    `"${(o.customer_name || "").replace(/"/g, '""')}"`,
    o.customer_phone || "N/A",
    o.purchase_date || "",
    o.amount || "0.00",
    o.shipping_status || o.status || "Pending",
  ]);

  const csvContent = "data:text/csv;charset=utf-8," + [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
  const encodedUri = encodeURI(csvContent);
  const link = document.createElement("a");
  link.setAttribute("href", encodedUri);
  link.setAttribute("download", `evex_integrated_orders_${new Date().toISOString().split("T")[0]}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const downloadTransactionsCSV = (items: SettlementTransaction[]) => {
  if (!items || items.length === 0) return;
  const headers = ["Txn Hash / Idempotency Key", "Ledger Event Type", "Debit Account", "Credit Account", "Amount", "Created At"];
  const rows = items.map((t) => [
    t.idempotency_key || `TXN-${t.id}`,
    `"${(t.event_type || "").replace(/"/g, '""')}"`,
    t.debit_account_type || "",
    t.credit_account_type || "",
    t.amount || "0.00",
    t.created_at || "",
  ]);

  const csvContent = "data:text/csv;charset=utf-8," + [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
  const encodedUri = encodeURI(csvContent);
  const link = document.createElement("a");
  link.setAttribute("href", encodedUri);
  link.setAttribute("download", `evex_settlement_ledger_${new Date().toISOString().split("T")[0]}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const ConnectedPartnerDashboard: React.FC<ConnectedPartnerDashboardProps> = ({
  partner,
  orders = [],
  syncing,
  onSync,
  onDisconnect,
  onBackToCatalog,
  onOpenLogs,
  onRetryOrderSync,
  currentPage = 1,
  totalPages = 1,
  totalCount = 0,
  pageSize = 20,
  onPageChange,
  onSearchChange,
  onStatusFilterChange,
  onDateRangeChange,
  stats,
  kycStatus,
  merchantId,
  settlementSummary,
  settlementTransactions = [],
  loadingLedger = false,
  ledgerPage = 1,
  ledgerTotalPages = 1,
  ledgerTotalCount = 0,
  onLedgerPageChange,
  onLedgerEventTypeChange,
}) => {
  // State for search and filter controls
  const [orderSearch, setOrderSearch] = useState("");
  const [orderStatusFilter, setOrderStatusFilter] = useState("All");
  const [partnerFilter, setPartnerFilter] = useState("All");
  const [dateFilter, setDateFilter] = useState("This Month");

  const [txnSearch, setTxnSearch] = useState("");
  const [ledgerTypeFilter, setLedgerTypeFilter] = useState("All");
  const [txnStatusFilter, setTxnStatusFilter] = useState("All");

  const [selectedOrders, setSelectedOrders] = useState<number[]>([]);

  // Pure dynamic Order List from API
  const activeOrders = orders;

  // Filtered Orders
  const filteredOrders = activeOrders.filter((order) => {
    const matchSearch =
      !orderSearch ||
      order.customer_name?.toLowerCase().includes(orderSearch.toLowerCase()) ||
      (order.order_id_display || String(order.id)).toLowerCase().includes(orderSearch.toLowerCase()) ||
      (order.first_item?.name || order.product_name || "").toLowerCase().includes(orderSearch.toLowerCase());

    const matchStatus =
      orderStatusFilter === "All" ||
      (order.shipping_status || order.status || "")
        .toLowerCase()
        .includes(orderStatusFilter.toLowerCase());

    return matchSearch && matchStatus;
  });

  // Pure dynamic Settlement Transactions from API
  const activeTransactions = settlementTransactions;

  const filteredTransactions = activeTransactions.filter((txn) => {
    const txnHash = txn.idempotency_key || String(txn.id || "");
    const ledgerType = txn.event_type || "";
    const matchSearch =
      !txnSearch ||
      txnHash.toLowerCase().includes(txnSearch.toLowerCase()) ||
      ledgerType.toLowerCase().includes(txnSearch.toLowerCase());

    const matchLedger =
      ledgerTypeFilter === "All" ||
      ledgerType.toLowerCase().includes(ledgerTypeFilter.toLowerCase());

    return matchSearch && matchLedger;
  });

  const handleSearchInput = (val: string) => {
    setOrderSearch(val);
    if (onSearchChange) onSearchChange(val);
  };

  const handleStatusSelect = (val: string) => {
    setOrderStatusFilter(val);
    if (onStatusFilterChange) onStatusFilterChange(val);
  };

  const toggleSelectAll = () => {
    if (selectedOrders.length === filteredOrders.length) {
      setSelectedOrders([]);
    } else {
      setSelectedOrders(filteredOrders.map((o) => o.id));
    }
  };

  const toggleSelectRow = (id: number) => {
    setSelectedOrders((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  // Helper renderer for status pill badges
  const renderStatusBadge = (statusStr: string) => {
    const s = statusStr.toLowerCase();
    if (s.includes("ready") || s.includes("shipped")) {
      return (
        <span
          className="badge px-3 py-2 fw-medium rounded-pill"
          style={{ backgroundColor: "#e0f2fe", color: "#0284c7" }}
        >
          ● Ready to Ship
        </span>
      );
    }
    if (s.includes("pending") || s.includes("new")) {
      return (
        <span
          className="badge px-3 py-2 fw-medium rounded-pill"
          style={{ backgroundColor: "#fef3c7", color: "#d97706" }}
        >
          ● Pending
        </span>
      );
    }
    if (s.includes("picked") || s.includes("processing")) {
      return (
        <span
          className="badge px-3 py-2 fw-medium rounded-pill"
          style={{ backgroundColor: "#f3e8ff", color: "#7c3aed" }}
        >
          ● Picked Up
        </span>
      );
    }
    if (s.includes("delivered") || s.includes("success")) {
      return (
        <span
          className="badge px-3 py-2 fw-medium rounded-pill"
          style={{ backgroundColor: "#dcfce7", color: "#16a34a" }}
        >
          ● Delivered
        </span>
      );
    }
    return (
      <span
        className="badge px-3 py-2 fw-medium rounded-pill"
        style={{ backgroundColor: "#fee2e2", color: "#dc2626" }}
      >
        ● Failed / Returned
      </span>
    );
  };

  // Render KYC badge
  const activeKyc = kycStatus || "APPROVED";

  return (
    <div className="d-flex flex-column gap-4">
      {/* Back button */}
      <div>
        <button
          type="button"
          onClick={onBackToCatalog}
          className="btn btn-link text-decoration-none text-muted p-0 d-inline-flex align-items-center gap-2 fw-medium small mb-2"
        >
          <LuArrowLeft size={16} />
          Back to All Integrations
        </button>
      </div>

      {/* 1. Connected Partner Top Header Bar */}
      <div className="bg-white rounded-4 p-4 border shadow-sm">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
          <div className="d-flex align-items-center gap-3">
            {/* Logo */}
            <div
              className="d-flex align-items-center justify-content-center rounded-3 p-2"
              style={{
                width: "48px",
                height: "48px",
                backgroundColor: partner.id === "evex" ? "#dcfce7" : partner.id === "ncm" ? "#ffedd5" : "#e0f2fe",
              }}
            >
              <div
                className="rounded-circle bg-success text-white d-flex align-items-center justify-content-center fw-bold"
                style={{ width: "32px", height: "32px", fontSize: "12px" }}
              >
                {partner.name.substring(0, 1).toUpperCase()}
              </div>
            </div>

            <div>
              <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                <h2 className="fs-4 fw-bold text-dark mb-0">{partner.name}</h2>
                <span
                  className="badge px-2.5 py-1.5 fw-medium rounded-pill d-inline-flex align-items-center gap-1"
                  style={{ backgroundColor: "#ecfdf5", color: "#059669", fontSize: "12px" }}
                >
                  <span className="rounded-circle bg-success" style={{ width: 6, height: 6 }} />
                  Connected
                </span>

                {/* Merchant ID from Store Preference API or Partner */}
                <span
                  className="px-2 py-1 rounded-2 text-muted fw-semibold border"
                  style={{ backgroundColor: "#f8fafc", fontSize: "11px" }}
                >
                  Merchant ID: {merchantId || partner.merchantId || "N/A"}
                </span>

                {/* KYC Status Badge from Partner / Preference API */}
                <span
                  className="badge px-2.5 py-1.5 fw-medium rounded-pill"
                  style={{
                    backgroundColor:
                      (kycStatus || partner.kyc_status) === "APPROVED"
                        ? "#dcfce7"
                        : (kycStatus || partner.kyc_status) === "PENDING"
                          ? "#fef3c7"
                          : "#fee2e2",
                    color:
                      (kycStatus || partner.kyc_status) === "APPROVED"
                        ? "#16a34a"
                        : (kycStatus || partner.kyc_status) === "PENDING"
                          ? "#d97706"
                          : "#dc2626",
                    fontSize: "11px",
                  }}
                >
                  KYC: {kycStatus || partner.kyc_status || "PENDING"}
                </span>
              </div>
              <p className="text-muted small mb-0">
                {partner.description || partner.networkDetails || "Automated Last-Mile Logistics"}
              </p>
            </div>
          </div>

          {/* Action buttons matching Figma */}
          <div className="d-flex align-items-center gap-3">
            <button
              type="button"
              onClick={onSync}
              disabled={syncing}
              className="btn border-0 rounded-3 px-3 py-2 fw-medium d-inline-flex align-items-center gap-2 small shadow-none"
              style={{ backgroundColor: "#eff6ff", color: "#1d4ed8" }}
            >
              <LuRefreshCw className={syncing ? "spin" : ""} size={14} />
              {syncing ? "Syncing..." : "Sync Now"}
            </button>
            <button
              type="button"
              onClick={onDisconnect}
              className="btn btn-link text-decoration-none p-0 fw-semibold text-danger d-inline-flex align-items-center gap-1.5 small shadow-none border-0"
              style={{ color: "#ef4444" }}
            >
              <LuPower size={14} />
              Disconnect
            </button>
          </div>
        </div>
      </div>

      {/* 2. Overview Section */}
      <div>
        <h3 className="fs-5 fw-bold text-dark mb-3">Overview</h3>
        <Row className="g-3">
          {/* Total */}
          <Col xl={2} md={3} sm={6}>
            <div
              className="bg-white rounded-4 p-3 border h-100 d-flex flex-column justify-content-between"
              style={{ boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)" }}
            >
              <div className="d-flex justify-content-between align-items-start mb-2">
                <span className="text-muted small fw-medium">Total Shipments</span>
                <LuFileText className="text-secondary" size={16} />
              </div>
              <div>
                <h4 className="fs-3 fw-bold text-dark mb-1">{stats?.total ?? 0}</h4>
                <span className="badge bg-primary-subtle text-primary fw-medium" style={{ fontSize: "11px" }}>
                  All Delivery Logs
                </span>
              </div>
            </div>
          </Col>

          {/* Synced */}
          <Col xl={2} md={3} sm={6}>
            <div
              className="bg-white rounded-4 p-3 border h-100 d-flex flex-column justify-content-between"
              style={{ boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)" }}
            >
              <div className="d-flex justify-content-between align-items-start mb-2">
                <span className="text-muted small fw-medium">Synced</span>
                <LuCheckCircle2 className="text-success" size={16} />
              </div>
              <div>
                <h4 className="fs-3 fw-bold text-success mb-1">{stats?.synced ?? 0}</h4>
                <span className="badge bg-success-subtle text-success fw-medium" style={{ fontSize: "11px" }}>
                  Successfully Synced
                </span>
              </div>
            </div>
          </Col>

          {/* Pending */}
          <Col xl={2} md={3} sm={6}>
            <div
              className="bg-white rounded-4 p-3 border h-100 d-flex flex-column justify-content-between"
              style={{ boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)" }}
            >
              <div className="d-flex justify-content-between align-items-start mb-2">
                <span className="text-muted small fw-medium">Pending</span>
                <LuClock className="text-warning" size={16} />
              </div>
              <div>
                <h4 className="fs-3 fw-bold text-dark mb-1">{stats?.pending ?? 0}</h4>
                <span className="badge bg-warning-subtle text-warning-emphasis fw-medium" style={{ fontSize: "11px" }}>
                  Awaiting EVEX State
                </span>
              </div>
            </div>
          </Col>

          {/* Failed */}
          <Col xl={2} md={3} sm={6}>
            <div
              className="bg-white rounded-4 p-3 border h-100 d-flex flex-column justify-content-between"
              style={{ boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)" }}
            >
              <div className="d-flex justify-content-between align-items-start mb-2">
                <span className="text-muted small fw-medium">Sync Failed</span>
                <LuAlertCircle className="text-danger" size={16} />
              </div>
              <div>
                <h4 className="fs-3 fw-bold text-danger mb-1">{stats?.failed ?? 0}</h4>
                <span className="badge bg-danger-subtle text-danger fw-medium" style={{ fontSize: "11px" }}>
                  Requires Retry
                </span>
              </div>
            </div>
          </Col>

          {/* In Transit */}
          <Col xl={2} md={3} sm={6}>
            <div
              className="bg-white rounded-4 p-3 border h-100 d-flex flex-column justify-content-between"
              style={{ boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)" }}
            >
              <div className="d-flex justify-content-between align-items-start mb-2">
                <span className="text-muted small fw-medium">In Transit</span>
                <LuBike className="text-info" size={16} />
              </div>
              <div>
                <h4 className="fs-3 fw-bold text-dark mb-1">{stats?.in_transit ?? 0}</h4>
                <span className="badge bg-info-subtle text-info-emphasis fw-medium" style={{ fontSize: "11px" }}>
                  Moving
                </span>
              </div>
            </div>
          </Col>

          {/* Out For Delivery */}
          <Col xl={2} md={3} sm={6}>
            <div
              className="bg-white rounded-4 p-3 border h-100 d-flex flex-column justify-content-between"
              style={{ boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)" }}
            >
              <div className="d-flex justify-content-between align-items-start mb-2">
                <span className="text-muted small fw-medium">Out for Delivery</span>
                <LuBike className="text-primary" size={16} />
              </div>
              <div>
                <h4 className="fs-3 fw-bold text-dark mb-1">{stats?.out_for_delivery ?? 0}</h4>
                <span className="badge bg-primary-subtle text-primary fw-medium" style={{ fontSize: "11px" }}>
                  Rider Out
                </span>
              </div>
            </div>
          </Col>

          {/* Delivered */}
          <Col xl={2} md={3} sm={6}>
            <div
              className="bg-white rounded-4 p-3 border h-100 d-flex flex-column justify-content-between"
              style={{ boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)" }}
            >
              <div className="d-flex justify-content-between align-items-start mb-2">
                <span className="text-muted small fw-medium">Delivered</span>
                <LuCheckCircle2 className="text-success" size={16} />
              </div>
              <div>
                <h4 className="fs-3 fw-bold text-success mb-1">{stats?.delivered ?? 0}</h4>
                <span className="badge bg-success-subtle text-success fw-medium" style={{ fontSize: "11px" }}>
                  Completed
                </span>
              </div>
            </div>
          </Col>

          {/* Cancelled */}
          <Col xl={2} md={3} sm={6}>
            <div
              className="bg-white rounded-4 p-3 border h-100 d-flex flex-column justify-content-between"
              style={{ boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)" }}
            >
              <div className="d-flex justify-content-between align-items-start mb-2">
                <span className="text-muted small fw-medium">Cancelled</span>
                <LuAlertCircle className="text-secondary" size={16} />
              </div>
              <div>
                <h4 className="fs-3 fw-bold text-dark mb-1">{stats?.cancelled ?? 0}</h4>
                <span className="badge bg-secondary-subtle text-secondary fw-medium" style={{ fontSize: "11px" }}>
                  Cancelled
                </span>
              </div>
            </div>
          </Col>

          {/* Returned / RTO */}
          <Col xl={2} md={3} sm={6}>
            <div
              className="bg-white rounded-4 p-3 border h-100 d-flex flex-column justify-content-between"
              style={{ boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)" }}
            >
              <div className="d-flex justify-content-between align-items-start mb-2">
                <span className="text-muted small fw-medium">Returned / RTO</span>
                <LuAlertCircle className="text-danger" size={16} />
              </div>
              <div>
                <h4 className="fs-3 fw-bold text-danger mb-1">{stats?.returned ?? 0}</h4>
                <span className="badge bg-danger-subtle text-danger fw-medium" style={{ fontSize: "11px" }}>
                  RTO / Returned
                </span>
              </div>
            </div>
          </Col>
        </Row>
      </div>

      {/* 3. Integrated Orders List Section */}
      <div className="bg-white rounded-4 p-4 border shadow-sm">
        <h3 className="fs-5 fw-bold text-dark mb-3">Integrated Orders List</h3>

        {/* Filter Toolbar */}
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-4">
          <div className="d-flex align-items-center gap-2 flex-grow-1" style={{ maxWidth: "340px" }}>
            <div className="input-group rounded-3">
              <span className="input-group-text bg-white border-end-0">
                <LuSearch className="text-muted" size={16} />
              </span>
              <Form.Control
                type="text"
                placeholder="Filter orders..."
                value={orderSearch}
                onChange={(e) => handleSearchInput(e.target.value)}
                className="border-start-0 shadow-none ps-0"
              />
            </div>
          </div>

          <div className="d-flex align-items-center gap-2 flex-wrap">
            {/* Status Filter */}
            <Dropdown>
              <Dropdown.Toggle variant="light" className="bg-white border text-secondary small d-flex align-items-center gap-2">
                <LuFilter size={14} />
                <span>Status: {orderStatusFilter}</span>
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={() => handleStatusSelect("All")}>All</Dropdown.Item>
                <Dropdown.Item onClick={() => handleStatusSelect("Ready to Ship")}>Ready to Ship / Shipped</Dropdown.Item>
                <Dropdown.Item onClick={() => handleStatusSelect("Pending")}>Pending / Processing</Dropdown.Item>
                <Dropdown.Item onClick={() => handleStatusSelect("Picked Up")}>Picked Up</Dropdown.Item>
                <Dropdown.Item onClick={() => handleStatusSelect("Delivered")}>Delivered</Dropdown.Item>
                <Dropdown.Item onClick={() => handleStatusSelect("Failed")}>Failed / Returned</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>

            {/* Date Filter */}
            <Dropdown>
              <Dropdown.Toggle variant="light" className="bg-white border text-secondary small d-flex align-items-center gap-2">
                <LuCalendar size={14} />
                <span>{dateFilter}</span>
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={() => setDateFilter("Last 7 days")}>Last 7 days</Dropdown.Item>
                <Dropdown.Item onClick={() => setDateFilter("Last 30 days")}>Last 30 days</Dropdown.Item>
                <Dropdown.Item onClick={() => setDateFilter("This Month")}>This Month</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>

            {/* Export / Download CSV Button */}
            <button
              type="button"
              onClick={() => downloadOrdersCSV(filteredOrders)}
              title="Export Orders CSV"
              className="btn btn-light bg-white border text-secondary p-2 d-flex align-items-center justify-content-center"
            >
              <LuDownload size={16} />
            </button>
          </div>
        </div>

        {/* Orders Table */}
        <div className="table-responsive border rounded-3 mb-3">
          <Table hover className="align-middle mb-0" style={{ fontSize: "14px" }}>
            <thead className="bg-light text-uppercase text-secondary" style={{ fontSize: "11px", letterSpacing: "0.05em" }}>
              <tr>
                <th className="py-3 px-3" style={{ width: "40px" }}>
                  <input
                    type="checkbox"
                    className="form-check-input rounded-circle shadow-none"
                    style={{ width: "18px", height: "18px", borderRadius: "50%", cursor: "pointer" }}
                    checked={selectedOrders.length === filteredOrders.length && filteredOrders.length > 0}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th className="py-3">Product</th>
                <th className="py-3">Order ID</th>
                <th className="py-3">Customer</th>
                <th className="py-3">Order Date</th>
                <th className="py-3">Amount</th>
                <th className="py-3">Status</th>
                <th className="py-3 text-end px-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.length > 0 ? (
                filteredOrders.map((order) => {
                  const isChecked = selectedOrders.includes(order.id);
                  return (
                    <tr key={order.id}>
                      <td className="px-3">
                        <input
                          type="checkbox"
                          className="form-check-input rounded-circle shadow-none"
                          style={{ width: "18px", height: "18px", borderRadius: "50%", cursor: "pointer" }}
                          checked={isChecked}
                          onChange={() => toggleSelectRow(order.id)}
                        />
                      </td>
                      <td>
                        <div className="d-flex align-items-center gap-3">
                          <img
                            src={order.product_image || "https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=120"}
                            alt="product"
                            className="rounded-3 object-fit-cover"
                            style={{ width: 42, height: 42 }}
                          />
                          <div>
                            <div className="fw-semibold text-dark" style={{ fontSize: "14px" }}>{order.product_name || "Integrated Item"}</div>
                            <div className="text-muted" style={{ fontSize: "12px" }}>{order.product_sku || "SKU: N/A"}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="fw-semibold text-primary" style={{ cursor: "pointer", fontSize: "14px" }}>
                          {order.order_id_display || `#ORD-${order.id}`}
                        </span>
                      </td>
                      <td>
                        <div>
                          <div className="fw-medium text-dark" style={{ fontSize: "14px" }}>{order.customer_name}</div>
                          <div className="text-muted" style={{ fontSize: "12px" }}>{order.customer_phone || "9876573210"}</div>
                        </div>
                      </td>
                      <td className="text-secondary" style={{ fontSize: "13px" }}>{order.purchase_date}</td>
                      <td className="fw-semibold text-dark" style={{ fontSize: "14px" }}>{order.amount}</td>
                      <td>{renderStatusBadge(order.shipping_status || order.status)}</td>
                      <td className="text-end px-3">
                        <Dropdown align="end">
                          <Dropdown.Toggle variant="light" className="btn btn-sm btn-link text-muted p-0 shadow-none border-0">
                            <LuMoreVertical size={16} />
                          </Dropdown.Toggle>
                          <Dropdown.Menu>
                            <Dropdown.Item onClick={() => onOpenLogs && onOpenLogs(order)}>
                              <LuFileText size={14} className="me-2 text-muted" />
                              View Delivery Logs
                            </Dropdown.Item>
                            {onRetryOrderSync && (
                              <Dropdown.Item onClick={() => onRetryOrderSync(order.id)}>
                                <LuRotateCw size={14} className="me-2 text-primary" />
                                Retry EVEX Sync
                              </Dropdown.Item>
                            )}
                          </Dropdown.Menu>
                        </Dropdown>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={8} className="text-center text-muted py-4">
                    No integrated orders found matching filters.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>

        {/* Orders Table Pagination */}
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 text-muted small">
          <div>
            Showing <strong>1 to {filteredOrders.length}</strong> of <strong>{totalCount || filteredOrders.length}</strong> orders
          </div>
          <div className="d-flex align-items-center gap-1">
            <button
              type="button"
              disabled={currentPage <= 1}
              onClick={() => onPageChange && onPageChange(currentPage - 1)}
              className="btn btn-sm btn-light border rounded-2 px-2 py-1"
            >
              <LuChevronLeft size={14} />
            </button>
            {Array.from({ length: Math.min(totalPages || 1, 5) }).map((_, i) => {
              const pNum = i + 1;
              return (
                <button
                  key={pNum}
                  type="button"
                  onClick={() => onPageChange && onPageChange(pNum)}
                  className={`btn btn-sm ${currentPage === pNum ? "btn-primary text-white" : "btn-light"} rounded-circle px-2 py-1`}
                  style={{ width: 28, height: 28, fontSize: 12 }}
                >
                  {pNum}
                </button>
              );
            })}
            {totalPages > 5 && <span>...</span>}
            <button
              type="button"
              disabled={currentPage >= totalPages}
              onClick={() => onPageChange && onPageChange(currentPage + 1)}
              className="btn btn-sm btn-light border rounded-2 px-2 py-1"
            >
              <LuChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>

      {/* 4. Payments & Courier Clearing Settlement Section */}
      <div className="bg-white rounded-4 p-4 border shadow-sm">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-3">
          <div>
            <h3 className="fs-5 fw-bold text-dark mb-1">Payments & Courier Clearing Settlement</h3>
            <p className="text-muted small mb-0">
              COD liquidity management, automated courier fee netting, and bank clearing ledger.
            </p>
          </div>
          <button
            type="button"
            onClick={() => downloadTransactionsCSV(filteredTransactions)}
            className="btn btn-light bg-white border text-dark fw-medium rounded-3 px-3 py-2 small d-inline-flex align-items-center gap-2"
          >
            <LuFileText size={16} />
            Download Tax Invoice
          </button>
        </div>

        {/* 5 Financial Stat Cards */}
        <Row className="g-3 mb-4">
          <Col lg={2.4} md={4} sm={6} className="col-lg-custom">
            <div className="bg-light-subtle rounded-3 p-3 border h-100">
              <span className="text-uppercase text-secondary fw-semibold small" style={{ fontSize: "10px", letterSpacing: "0.05em" }}>
                Total Delivery Earnings
              </span>
              <h4 className="fs-5 fw-bold text-dark my-1">
                Rs. {settlementSummary?.total_shipment_value ? parseFloat(settlementSummary.total_shipment_value).toLocaleString() : "0.00"}
              </h4>
              <span className="text-muted small">
                Delivery Charges: Rs. {settlementSummary?.delivery_charges ? parseFloat(settlementSummary.delivery_charges).toLocaleString() : "0.00"}
              </span>
            </div>
          </Col>

          <Col lg={2.4} md={4} sm={6} className="col-lg-custom">
            <div className="bg-light-subtle rounded-3 p-3 border h-100">
              <span className="text-uppercase text-secondary fw-semibold small" style={{ fontSize: "10px" }}>
                COD on Account
              </span>
              <h4 className="fs-5 fw-bold text-success my-1">
                Rs. {settlementSummary?.cod_shipments ? parseFloat(settlementSummary.cod_shipments).toLocaleString() : "0.00"}
              </h4>
              <span className="badge bg-success-subtle text-success fw-medium" style={{ fontSize: "11px" }}>
                🟢 Rider Collected: Rs. {settlementSummary?.cod_collected_by_platform_rider ? parseFloat(settlementSummary.cod_collected_by_platform_rider).toLocaleString() : "0.00"}
              </span>
            </div>
          </Col>

          <Col lg={2.4} md={4} sm={6} className="col-lg-custom">
            <div className="bg-light-subtle rounded-3 p-3 border h-100">
              <span className="text-uppercase text-secondary fw-semibold small" style={{ fontSize: "10px" }}>
                Amount Net Price
              </span>
              <h4 className="fs-5 fw-bold text-dark my-1">
                Rs. {settlementSummary?.net_platform_liability ? parseFloat(settlementSummary.net_platform_liability).toLocaleString() : "0.00"}
              </h4>
              <span className="text-muted small">
                Payout Completed: Rs. {settlementSummary?.payout_complete ? parseFloat(settlementSummary.payout_complete).toLocaleString() : "0.00"}
              </span>
            </div>
          </Col>

          <Col lg={2.4} md={4} sm={6} className="col-lg-custom">
            <div className="bg-light-subtle rounded-3 p-3 border h-100">
              <span className="text-uppercase text-secondary fw-semibold small" style={{ fontSize: "10px" }}>
                Pending Settlement
              </span>
              <h4 className="fs-5 fw-bold text-warning-emphasis my-1">
                Rs. {settlementSummary?.payout_in_process ? parseFloat(settlementSummary.payout_in_process).toLocaleString() : "0.00"}
              </h4>
              <span className="text-warning-emphasis small fw-medium">Payout in Process</span>
            </div>
          </Col>

          <Col lg={2.4} md={4} sm={6} className="col-lg-custom">
            <div className="bg-light-subtle rounded-3 p-3 border h-100">
              <span className="text-uppercase text-secondary fw-semibold small" style={{ fontSize: "10px" }}>
                Scheduled Balance
              </span>
              <h4 className="fs-5 fw-bold text-primary my-1">
                Rs. {settlementSummary?.delivery_charges ? parseFloat(settlementSummary.delivery_charges).toLocaleString() : "0.00"}
              </h4>
              <span className="text-muted small">
                Penalties/Adjustments: Rs. {settlementSummary?.penalties ? parseFloat(settlementSummary.penalties).toLocaleString() : "0.00"}
              </span>
            </div>
          </Col>
        </Row>
      </div>

      {/* 5. Recent Settlement Transactions Section */}
      <div className="bg-white rounded-4 p-4 border shadow-sm">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
          <div className="d-flex align-items-center gap-2">
            <h3 className="fs-5 fw-bold text-dark mb-0">Recent Settlement Transactions</h3>
            <span className="badge bg-light text-secondary border fw-medium px-2 py-1" style={{ fontSize: "12px" }}>
              Settlement Ledger
            </span>
          </div>
        </div>

        {/* Settlement Filters */}
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-4">
          <div className="d-flex align-items-center gap-2 flex-grow-1" style={{ maxWidth: "340px" }}>
            <div className="input-group">
              <span className="input-group-text bg-white border-end-0">
                <LuSearch className="text-muted" size={16} />
              </span>
              <Form.Control
                type="text"
                placeholder="Filter transactions or orders..."
                value={txnSearch}
                onChange={(e) => setTxnSearch(e.target.value)}
                className="border-start-0 shadow-none ps-0"
              />
            </div>
          </div>

          <div className="d-flex align-items-center gap-2 flex-wrap">
            <Dropdown>
              <Dropdown.Toggle variant="light" className="bg-white border text-secondary small">
                Ledger: {ledgerTypeFilter}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={() => { setLedgerTypeFilter("All"); if (onLedgerEventTypeChange) onLedgerEventTypeChange(""); }}>All</Dropdown.Item>
                <Dropdown.Item onClick={() => { setLedgerTypeFilter("COD_COLLECTED_BY_PLATFORM_RIDER_FUND"); if (onLedgerEventTypeChange) onLedgerEventTypeChange("COD_COLLECTED_BY_PLATFORM_RIDER_FUND"); }}>COD Collection</Dropdown.Item>
                <Dropdown.Item onClick={() => { setLedgerTypeFilter("COURIER_SHIPPING_FEE_DEDUCTION"); if (onLedgerEventTypeChange) onLedgerEventTypeChange("COURIER_SHIPPING_FEE_DEDUCTION"); }}>Shipping Fee</Dropdown.Item>
                <Dropdown.Item onClick={() => { setLedgerTypeFilter("MERCHANT_BATCH_GROSS_PAYOUT"); if (onLedgerEventTypeChange) onLedgerEventTypeChange("MERCHANT_BATCH_GROSS_PAYOUT"); }}>Direct Payout</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>

            <button
              type="button"
              onClick={() => downloadTransactionsCSV(filteredTransactions)}
              title="Export Ledger CSV"
              className="btn btn-light bg-white border text-secondary p-2"
            >
              <LuDownload size={16} />
            </button>
          </div>
        </div>

        {/* Transactions Table */}
        <div className="table-responsive border rounded-3 mb-3">
          <Table hover className="align-middle mb-0" style={{ fontSize: "14px" }}>
            <thead className="bg-light text-uppercase text-secondary" style={{ fontSize: "11px" }}>
              <tr>
                <th className="py-3 px-3">Txn Hash</th>
                <th className="py-3">Ledger Type</th>
                <th className="py-3">Debit / Credit Account</th>
                <th className="py-3">Amount</th>
                <th className="py-3 text-end px-3">Settled Timestamp</th>
              </tr>
            </thead>
            <tbody>
              {filteredTransactions.length > 0 ? (
                filteredTransactions.map((txn) => {
                  const isNeg = parseFloat(txn.amount || "0") < 0;
                  return (
                    <tr key={txn.id}>
                      <td className="px-3 fw-mono text-secondary small">{txn.idempotency_key || `TXN-${txn.id}`}</td>
                      <td className="text-dark fw-medium">{txn.event_type}</td>
                      <td className="text-muted small">
                        {txn.debit_account_type ? `${txn.debit_account_type} → ` : ""}{txn.credit_account_type || "N/A"}
                      </td>
                      <td>
                        <span className={`fw-bold ${isNeg ? "text-danger" : "text-success"}`}>
                          {isNeg ? "" : "+"}NPR {parseFloat(txn.amount || "0").toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </td>
                      <td className="text-end px-3 text-muted small">
                        {txn.created_at ? new Date(txn.created_at).toLocaleString() : "N/A"}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={5} className="text-center text-muted py-4">
                    {loadingLedger ? "Loading ledger entries..." : "No settlement transactions found."}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>

        {/* Transactions Table Pagination */}
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 text-muted small">
          <div>
            Showing <strong>1 to {filteredTransactions.length}</strong> of <strong>{ledgerTotalCount || filteredTransactions.length}</strong> transactions
          </div>
          <div className="d-flex align-items-center gap-1">
            <button
              type="button"
              disabled={ledgerPage <= 1}
              onClick={() => onLedgerPageChange && onLedgerPageChange(ledgerPage - 1)}
              className="btn btn-sm btn-light border rounded-2 px-2 py-1"
            >
              <LuChevronLeft size={14} />
            </button>
            {Array.from({ length: Math.min(ledgerTotalPages || 1, 5) }).map((_, i) => {
              const pNum = i + 1;
              return (
                <button
                  key={pNum}
                  type="button"
                  onClick={() => onLedgerPageChange && onLedgerPageChange(pNum)}
                  className={`btn btn-sm ${ledgerPage === pNum ? "btn-primary text-white" : "btn-light"} rounded-circle px-2 py-1`}
                  style={{ width: 28, height: 28, fontSize: 12 }}
                >
                  {pNum}
                </button>
              );
            })}
            <button
              type="button"
              disabled={ledgerPage >= ledgerTotalPages}
              onClick={() => onLedgerPageChange && onLedgerPageChange(ledgerPage + 1)}
              className="btn btn-sm btn-light border rounded-2 px-2 py-1"
            >
              <LuChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(ConnectedPartnerDashboard);
